import { PropType, defineComponent, h } from 'vue';
import { Dialog } from '@phi/components';
import { Vector3 } from 'three';
import FormSelect, { Entry } from '../FormSelect';
import { vif1 } from '@phi/util/vuetils';
import {
    display_set_vertex_xyz,
    uid_deselect_all_objects,
    uid_select_object,
    uid_move_objects_to_location,
} from '../../ui_display';
import { FormNumber, FormWithLabels, Label } from '@components/form';
import { uil_get_body_of_vertex, uil_select_body } from '../../ui_logic';
import {
    suppress_animate,
    unsuppress_animate_if_ready,
    get_display_vector_from_unitless_vector,
    get_unitless_vector_from_display_vector,
    get_display_unit_from_unitless_value,
} from '../../utility';
import { Flex } from '@phi/components/layout';
import { round_decimals } from '@features/decimal-approximation';
import { enumerate, map } from '@vaaas/fpts/iter';
import { pipe } from '@vaaas/fpts/function';
import { of } from '@vaaas/fpts/array';
import { IFrontendServiceContainer } from '@phi/frontend/service-container';

type Operation = 'leave' | 'set' | 'first';

const operations: Array<Entry<Operation>> = [
    { value: 'leave', text: 'Leave as is' },
    { value: 'set', text: 'Give value' },
    { value: 'first', text: 'Same as first vertex' },
];

const SelectOperation = FormSelect<Operation>();

type MoveOperation = 'vertex' | 'selected_objects' | 'entire_body';

const move_operations: Array<Entry<MoveOperation>> = [
    { value: 'vertex', text: 'Only this vertex' },
    { value: 'selected_objects', text: 'All selected objects' },
    { value: 'entire_body', text: 'Entrire body' },
];

const SelectMoveOperation = FormSelect<MoveOperation>();

export default defineComponent({
    props: {
        show: { required: true, type: Boolean },
        selected_vertices_count: { required: true, type: Number },
        app: { required: true, type: Object as PropType<IFrontendServiceContainer> },
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    data() {
        return {
            x_op: 'leave' as Operation,
            y_op: 'leave' as Operation,
            z_op: 'leave' as Operation,

            x: 0,
            y: 0,
            z: 0,

            move_operation: 'vertex' as MoveOperation,

            selected_vertex_coordinates: [] as Array<readonly [number, string, string, string]>,
        };
    },

    computed: {
        only_one_vertex_is_selected() {
            return this.selected_vertices_count === 1;
        },

        body() {
            return this.only_one_vertex_is_selected
                ? this.single_vertex_body
                : this.multiple_vertices_body;
        },

        single_vertex_body() {
            return h(FormWithLabels, {
                gap: 'tight',
                width: '3rem',
            }, () => [
                h(Label, null, {
                    label: () => h('span',  'X'),
                    input: () => h(FormNumber, {
                        id: 'vertex_xyz_xvalue',
                        value: this.x,
                        change: true,
                        'onUpdate:value': x => this.x = round_decimals(x),
                    }),
                }),

                h(Label, null, {
                    label: () => h('span', 'Y'),
                    input: () => h(FormNumber, {
                        id: 'vertex_xyz_yvalue',
                        value: this.y,
                        change: true,
                        'onUpdate:value': x => this.y = round_decimals(x),
                    }),
                }),

                h(Label, null, {
                    label: () => h('span', 'Z'),
                    input: () => h(FormNumber, {
                        id: 'vertex_xyz_zvalue',
                        value: this.z,
                        change: true,
                        'onUpdate:value': x => this.z = round_decimals(x),
                    }),
                }),

                h(Label, null, {
                    label: () => h('span', 'Move'),
                    input: () => h(SelectMoveOperation, {
                        value: this.move_operation,
                        'onUpdate:value': x => this.move_operation = x,
                        options: move_operations,
                    }),
                }),
            ]);
        },

        multiple_vertices_body() {
            return [
                h(FormWithLabels, {
                    gap: 'tight',
                    width: '2rem',
                }, () => [
                    h(Label, null, {
                        label: () => h('span', 'X'),
                        input: () => h(Flex, { direction: 'row', gap: 'tight', equally: true }, () => [
                            h(SelectOperation, {
                                options: operations,
                                value: this.x_op,
                                'onUpdate:value': x => this.x_op = x,
                            }),

                            vif1(this.x_op === 'set')(() => h(FormNumber, {
                                value: this.x,
                                'onUpdate:value': x => this.x = round_decimals(x),
                                change: true,
                            })),
                        ])
                    }),

                    h(Label, null, {
                        label: () => h('span', 'Y'),
                        input: () => h(Flex, { direction: 'row', gap: 'tight', equally: true }, () => [
                            h(SelectOperation, {
                                options: operations,
                                value: this.y_op,
                                'onUpdate:value': x => this.y_op = x,
                            }),

                            vif1(this.y_op === 'set')(() => h(FormNumber, {
                                value: this.y,
                                'onUpdate:value': x => this.y = round_decimals(x),
                                change: true,
                            })),
                        ]),
                    }),

                    h(Label, null, {
                        label: () => h('span', 'Z'),
                        input: () => h(Flex, { direction: 'row', gap: 'tight', equally: true }, () => [
                            h(SelectOperation, {
                                options: operations,
                                value: this.z_op,
                                'onUpdate:value': x => this.z_op = x,
                            }),

                            vif1(this.z_op === 'set')(() => h(FormNumber, {
                                value: this.z,
                                'onUpdate:value': x => this.z = round_decimals(x),
                                change: true,
                            })),
                        ]),
                    }),
                ]),

                h('div', { class: 'mt-5' }, [
                    h('p', 'Current coordinates of selected vertex/vertices (in order of selection):'),
                    h('section', this.selected_vertex_coordinates.map(([i, x, y, z]) =>
                        h('div', `Vertex ${i} (${x}, ${y}, ${z})`))),
                ]),
            ];
        },
    },

    methods: {
        cancel() {
            this.$emit('update:show', false);
        },

        confirm() {
            if (uid_selected_vertices.length > 1) {
                display_set_vertex_xyz(
                    this.x.toString(),
                    this.y.toString(),
                    this.z.toString(),
                    this.x_op,
                    this.y_op,
                    this.z_op,
                );
            } else {
                const vid = uid_selected_vertices.firstKey();
                if (!vid) return;
                suppress_animate();
                if (this.move_operation === 'vertex') {
                    uid_deselect_all_objects();
                    uid_select_object(vid);
                } else if (this.move_operation === 'entire_body') {
                    uid_deselect_all_objects();
                    const bid = uil_get_body_of_vertex(vid);
                    uil_select_body(bid);
                }
                unsuppress_animate_if_ready();
                uid_move_objects_to_location(vid, get_unitless_vector_from_display_vector(new Vector3(this.x, this.y, this.z)));
            }

            this.$emit('update:show', false);
        },

        reset() {
            const coords = first_vertex_coordinates();
            this.x_op = this.y_op = this.z_op = this.only_one_vertex_is_selected ? 'set' : 'leave';
            this.x = round_decimals(coords.x);
            this.y = round_decimals(coords.y);
            this.z = round_decimals(coords.z);
            this.move_operation = 'vertex';
            this.selected_vertex_coordinates = selected_vertex_coordinates(this.app.settings.coordinate_decimals);
        },
    },

    watch: {
        show() {
            this.reset();
        }
    },

    render() {
        return h(Dialog, {
            show: this.show,
            'onUpdate:show': () => this.$emit('update:show', false),
            close_label: 'Cancel',
            onClose: this.cancel,
            onConfirm: this.confirm,
            confirm_label: 'Set',
            id: 'setVertexXYZModal',
            title: `New coordinates of selected ${this.only_one_vertex_is_selected ? 'vertex' : 'vertices'}`,
            width: 'extra-narrow',
        }, () => this.body);
    }
});

function first_vertex_coordinates(): Vector3 {
    const empty = new Vector3(0, 0, 0);
    const vid = uid_selected_vertices.firstKey();
    if (!vid) return empty;
    const uidv = uid_vertices.getItem(vid);
    if (!uidv) return empty;
    return get_display_vector_from_unitless_vector(uidv.object3d!.position);
}

const selected_vertex_coordinates = (decimals: number) => pipe(
    uid_selected_vertices.values(),
    enumerate,
    map(([i, x]) => [
        i,
        get_display_unit_from_unitless_value(x.object3d!.position.x)
            .toFixed(decimals),
        get_display_unit_from_unitless_value(x.object3d!.position.y)
            .toFixed(decimals),
        get_display_unit_from_unitless_value(x.object3d!.position.z)
            .toFixed(decimals),
    ] as const),
    of,
);
